<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4 mb-0">{{ $t('walletDetail.title') }}</h1>
          <div>
            <v-btn @click="editWallet" class="mr-2" color="primary" prepend-icon="mdi-pencil">
              {{ $t('walletDetail.edit') }}
            </v-btn>
            <v-btn @click="$router.push('/wallets')" variant="text" prepend-icon="mdi-arrow-left">
              {{ $t('walletDetail.backToWallets') }}
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>
    
    <!-- Wallet Information, Balance Breakdown, and Balance Chart in one row -->
    <v-row class="mb-4">
      <!-- Wallet Information Card -->
      <v-col cols="12" md="4">
        <v-card>
          <v-card-text>
            <!-- Name, Address, Exchange - left label, right value -->
            <div v-if="wallet.name" class="d-flex justify-space-between align-center py-2 border-b">
              <span class="text-body-2 text-grey">{{ $t('walletDetail.name') }}</span>
              <span class="text-body-1 font-weight-medium">{{ wallet.name }}</span>
            </div>
            <div v-if="wallet.address" class="d-flex justify-space-between align-center py-2 border-b">
              <span class="text-body-2 text-grey">{{ $t('walletDetail.address') }}</span>
              <span class="text-body-2 text-wrap text-right" style="max-width: 60%;">{{ wallet.address }}</span>
            </div>
            <div class="d-flex justify-space-between align-center py-2 border-b">
              <span class="text-body-2 text-grey">{{ $t('walletDetail.exchange') }}</span>
              <span class="text-body-1 font-weight-medium">{{ wallet.exchange?.name || '-' }}</span>
            </div>
            
            <!-- Balance, Coin in one row -->
            <v-row class="mt-2">
              <v-col cols="6" sm="6">
                <div class="text-caption text-grey">{{ $t('walletDetail.coin') }}</div>
                <div class="text-body-1 font-weight-medium">{{ wallet.coin?.symbol }} - {{ wallet.coin?.name }}</div>
              </v-col>
              <v-col cols="6" sm="6">
                <div class="text-caption text-center text-grey">{{ $t('walletDetail.balance') }}</div>
                <div class="text-body-1 text-right font-weight-medium">{{ formatAmount(displayBalance) }}</div>
              </v-col>
            </v-row>
            <div v-if="wallet.office" class="mt-2">
              <div class="text-caption text-grey">{{ $t('walletDetail.office') }}</div>
              <div class="text-body-1 font-weight-medium">{{ wallet.office?.name }}</div>
            </div>
          </v-card-text>
        </v-card>
        
        <!-- Date Filters and Period Buttons -->
        <div class="filters-wrapper mt-4">
          <v-toolbar class="mb-0">
            <v-toolbar-title class="text-subtitle-1 mr-4">{{ $t('walletDetail.balanceAnalysisPeriod') }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <div class="date-from-wrapper mr-2">
              <v-text-field
                v-model="filterDateFrom"
                :label="$t('walletDetail.dateFrom')"
                type="date"
                density="compact"
                variant="outlined"
                hide-details
                style="max-width: 160px;"
                @update:model-value="onDateFilterChange"
              />
            </div>
            <v-text-field
              v-model="filterDateTo"
              :label="$t('walletDetail.dateTo')"
              type="date"
              density="compact"
              variant="outlined"
              hide-details
              class="mr-2"
              style="max-width: 160px;"
              @update:model-value="onDateFilterChange"
            />
          </v-toolbar>
          <div class="period-buttons-container">
            <v-btn-toggle v-model="datePeriod" :mandatory="false" class="period-buttons">
              <v-btn value="day" size="small">{{ $t('walletDetail.day') }}</v-btn>
              <v-btn value="week" size="small">{{ $t('walletDetail.week') }}</v-btn>
              <v-btn value="month" size="small">{{ $t('walletDetail.month') }}</v-btn>
              <v-btn value="custom" size="small">{{ $t('walletDetail.custom') }}</v-btn>
            </v-btn-toggle>
          </div>
        </div>
      </v-col>
      
      <!-- Balance Breakdown Section -->
      <v-col cols="12" md="4">
        <v-card>
          <v-card-text>
            <v-table v-if="balanceBreakdown" density="compact">
              <tbody>
                <tr>
                  <td class="font-weight-bold">{{ $t('walletDetail.startingBalance') }}</td>
                  <td class="text-right">{{ formatAmount(balanceBreakdown.starting_balance) }}</td>
                </tr>
                <tr>
                  <td class="text-success">{{ $t('walletDetail.incomingTransactions') }}</td>
                  <td class="text-right text-success">+{{ formatAmount(balanceBreakdown.incoming_transactions) }}</td>
                </tr>
                <tr>
                  <td class="text-error">{{ $t('walletDetail.outgoingTransactions') }}</td>
                  <td class="text-right text-error">-{{ formatAmount(balanceBreakdown.outgoing_transactions) }}</td>
                </tr>
                <tr>
                  <td class="text-error">{{ $t('walletDetail.expenses') }}</td>
                  <td class="text-right text-error">-{{ formatAmount(balanceBreakdown.total_expenses) }}</td>
                </tr>
                <tr>
                  <td class="text-success">{{ $t('walletDetail.settlementsToWallet') }}</td>
                  <td class="text-right text-success">+{{ formatAmount(balanceBreakdown.settlements_to_wallet) }}</td>
                </tr>
                <tr>
                  <td class="text-error">{{ $t('walletDetail.settlementsToInvestor') }}</td>
                  <td class="text-right text-error">-{{ formatAmount(balanceBreakdown.settlements_to_investor) }}</td>
                </tr>
                <tr class="border-top">
                  <td class="font-weight-bold">{{ $t('walletDetail.netChange') }}</td>
                  <td class="text-right font-weight-bold" :class="balanceBreakdown.net_change >= 0 ? 'text-success' : 'text-error'">
                    {{ balanceBreakdown.net_change >= 0 ? '+' : '' }}{{ formatAmount(balanceBreakdown.net_change) }}
                  </td>
                </tr>
                <tr class="border-top">
                  <td class="font-weight-bold text-h6">{{ $t('walletDetail.endingBalance') }}</td>
                  <td class="text-right font-weight-bold text-h6">
                    {{ formatAmount(balanceBreakdown.ending_balance) }}
                  </td>
                </tr>
              </tbody>
            </v-table>
            <div v-else class="text-center text-grey">
              {{ $t('walletDetail.selectDateRangeBreakdown') }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <!-- Balance Chart -->
      <v-col cols="12" md="4">
        <v-card>
          <v-card-text style="height: 300px">
            <Line v-if="chartData" :data="chartData" :options="chartOptions" />
            <div v-else class="text-center text-grey mt-8">
              {{ $t('walletDetail.selectDateRangeChart') }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Edit Wallet Dialog -->
    <v-dialog v-model="showEditDialog" max-width="500" @update:model-value="(val) => { if (!val) resetWalletForm(); }">
      <v-card>
        <v-card-title>{{ $t('walletDetail.edit') }}</v-card-title>
        <v-card-text>
          <v-form>
            <v-text-field 
              v-model="walletForm.name" 
              :label="$t('walletDetail.walletName')" 
              :placeholder="$t('walletDetail.walletNamePlaceholder')"
              clearable
            />
            <v-textarea
              v-model="walletForm.address"
              :label="$t('walletDetail.address')"
              :placeholder="$t('walletDetail.addressPlaceholder')"
              rows="3"
              class="mt-2"
              clearable
            />
            <v-select
              v-model="walletForm.exchange_id"
              :items="exchanges"
              item-title="name"
              item-value="id"
              :label="$t('walletDetail.exchange')"
              clearable
              class="mt-2"
            />
            <v-select
              v-model="walletForm.coin_id"
              :items="coins"
              item-title="name"
              item-value="id"
              :label="$t('walletDetail.coin')"
              class="mt-2"
            />
            <v-checkbox
              v-model="walletForm.office"
              :label="$t('walletDetail.isPhysicalOffice')"
              class="mt-2"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showEditDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveWallet">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Transaction Dialog -->
    <TransactionEditDialog
      v-model="showEditTransactionDialog"
      :transaction="editingTransaction"
      :coins="coins"
      :wallets="transactionWallets"
      :users="users"
      :clients="clients"
      @save="handleSaveTransaction"
      @cancel="handleCancelTransaction"
    />

    <!-- Edit Expense Dialog -->
    <ExpenseEditDialog
      v-model="showEditExpenseDialog"
      :expense="editingExpense"
      :wallets="allWallets"
      :expenses-types="expensesTypes"
      :users="users"
      @save="handleSaveExpense"
      @cancel="handleCancelExpense"
    />

    <!-- Edit Settlement Dialog -->
    <SettlementEditDialog
      v-model="showEditSettlementDialog"
      :settlement="editingSettlement"
      :investors="investors"
      :wallets="allWallets"
      :users="users"
      @save="handleSaveSettlement"
      @cancel="handleCancelSettlement"
    />

    <!-- Balance History Section -->
    <v-row class="mb-8">
      <v-col cols="12">
        <h2 class="text-h5 mb-4">{{ $t('walletDetail.balanceHistory') }}</h2>
        <v-card v-if="loading">
          <v-card-text>
            <v-progress-linear indeterminate color="primary"></v-progress-linear>
            <p class="text-center mt-2">{{ $t('walletDetail.loadingHistory') }}</p>
          </v-card-text>
        </v-card>
        <v-card v-else-if="balanceHistory.length === 0">
          <v-card-text>
            <p class="text-center text-grey">{{ $t('walletDetail.noBalanceChanges') }}</p>
          </v-card-text>
        </v-card>
        <v-table v-else density="compact">
          <thead>
            <tr>
              <th>{{ $t('walletDetail.dateColumn') }}</th>
              <th>{{ $t('walletDetail.typeColumn') }}</th>
              <th>{{ $t('walletDetail.descriptionColumn') }}</th>
              <th class="text-right">{{ $t('walletDetail.balanceBeforeColumn') }}</th>
              <th class="text-right">{{ $t('walletDetail.balanceChangeColumn') }}</th>
              <th class="text-right">{{ $t('walletDetail.balanceAfterColumn') }}</th>
              <th class="text-right" v-if="hasSendFee">{{ $t('walletDetail.sendFeeColumn') }}</th>
              <th>{{ $t('walletDetail.userColumn') }}</th>
              <th>{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in sortedBalanceHistory" :key="item.id">
              <td>{{ formatDateTime(item.date) }}</td>
              <td>
                <v-chip 
                  :color="getTypeColor(item.type)" 
                  size="small"
                  variant="tonal"
                >
                  {{ getTypeLabel(item.type) }}
                </v-chip>
              </td>
              <td>{{ item.description }}</td>
              <td class="text-right font-weight-medium">
                {{ formatAmount(item.balance_before) }}
              </td>
              <td class="text-right" :class="item.balance_change >= 0 ? 'text-success' : 'text-error'">
                {{ item.balance_change >= 0 ? '+' : '' }}{{ formatAmount(item.balance_change) }}
              </td>
              <td class="text-right font-weight-medium">
                {{ formatAmount(item.balance_after) }}
              </td>
              <td class="text-right" v-if="hasSendFee">
                {{ item.details?.send_fee ? formatAmount(item.details.send_fee) : '-' }}
              </td>
              <td>{{ item.user_email || '-' }}</td>
              <td>
                <v-btn 
                  variant="plain" 
                  size="small" 
                  icon="mdi-pencil" 
                  @click="editItem(item)"
                  :title="$t('walletDetail.editItem')"
                ></v-btn>
                <v-btn 
                  variant="plain" 
                  size="small" 
                  color="error" 
                  icon="mdi-delete" 
                  @click="deleteItem(item)"
                  :title="$t('walletDetail.deleteItem')"
                ></v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { apiGet, apiDelete, apiPut, apiPost, getErrorMessage } from '../utils/api'
import { formatDateForDisplay, getDateRangeForPeriod, formatDateToYYYYMMDD, dateStringToISO, getTodayString } from '../utils/date'
import { Line } from 'vue-chartjs'
import { Chart, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend, Filler } from 'chart.js'
import TransactionEditDialog from './dialogs/TransactionEditDialog.vue'
import ExpenseEditDialog from './dialogs/ExpenseEditDialog.vue'
import SettlementEditDialog from './dialogs/SettlementEditDialog.vue'

Chart.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend, Filler)

const { t } = useI18n()

interface Coin {
  id: number
  name: string
  symbol: string
}

interface Exchange {
  id: number
  name: string
}

interface Wallet {
  id: number
  exchange: Exchange | null
  office: boolean
  coin: Coin
  balance: number
  display_name: string
  name?: string | null
  address?: string | null
}

interface BalanceHistoryItem {
  id: string
  record_id: number
  type: 'transaction' | 'expense' | 'settlement'
  date: string
  description: string
  details: any
  balance_change: number
  balance_before: number
  balance_after: number
  user_email: string | null
  send_fee?: number
}

interface BalanceBreakdown {
  starting_balance: number
  ending_balance: number
  incoming_transactions: number
  outgoing_transactions: number
  total_expenses: number
  settlements_to_wallet: number
  settlements_to_investor: number
  net_change: number
  daily_balances: Array<{ date: string; balance: number }>
}

const route = useRoute()
const router = useRouter()
const wallet = ref<Wallet>({
  id: 0,
  exchange: null,
  office: null,
  coin: { id: 0, name: '', symbol: '' },
  balance: 0,
  display_name: '',
  name: null,
  address: null
})
const balanceHistory = ref<BalanceHistoryItem[]>([])
const balanceBreakdown = ref<BalanceBreakdown | null>(null)
const loading = ref(true)

// Display balance: use period ending balance if available, otherwise total calculated balance
const displayBalance = computed(() => {
  if (balanceBreakdown.value && balanceBreakdown.value.ending_balance !== undefined) {
    return balanceBreakdown.value.ending_balance
  }
  return wallet.value.calculated_balance ?? 0
})

// Note: USDT value calculation removed as usdt_rate is no longer stored in wallets

// Check if any item has send_fee
const hasSendFee = computed(() => {
  return balanceHistory.value.some(item => item.details?.send_fee && item.details.send_fee > 0)
})

// Sort balance history by date (newest first)
const sortedBalanceHistory = computed(() => {
  return [...balanceHistory.value].sort((a, b) => {
    const dateA = new Date(a.date).getTime()
    const dateB = new Date(b.date).getTime()
    return dateA - dateB  // Asc order (oldest first)
  })
})

// Edit wallet dialog
const showEditDialog = ref(false)
const coins = ref<Coin[]>([])
const exchanges = ref<Exchange[]>([])
const walletForm = ref({
  exchange_id: null as number | null,
  office: false as boolean,
  coin_id: null as number | null,
  name: '' as string | null,
  address: '' as string | null
})

// Edit item dialogs
const showEditTransactionDialog = ref(false)
const showEditExpenseDialog = ref(false)
const showEditSettlementDialog = ref(false)
const editingTransaction = ref<any>(null)
const editingExpense = ref<any>(null)
const editingSettlement = ref<any>(null)

// Data for edit forms
const allWallets = ref<any[]>([])
const users = ref<any[]>([])
const expensesTypes = ref<any[]>([])
const investors = ref<any[]>([])

// Transaction form
const transactionForm = ref({
  id: null as number | null,
  client_id: null as number | null,
  from_wallet_id: null as number | null,
  sold_coin_id: null as number | null,
  sold_amount: 0,
  sold_rate: 0,
  to_wallet_id: null as number | null,
  received_coin_id: null as number | null,
  received_amount: 0,
  received_rate: 0,
  user_id: null as number | null,
  send_fee: 0,
  receive_our_rate: 0,
  estimated_profit: 0,
  transaction_date: null as string | null,
})

// Transaction form search fields
const clientSearch = ref('')
const fromWalletSearch = ref('')
const toWalletSearch = ref('')
const clients = ref<any[]>([])
const transactionWallets = ref<any[]>([])
const isCalculatingReceivedAmount = ref(false)
const isCalculatingProfit = ref(false)
const isCalculatingReceiveOurRate = ref(false)

// Expense form
const expenseForm = ref({
  id: null as number | null,
  wallet_id: null as number | null,
  expenses_type_id: null as number | null,
  user_id: null as number | null,
  amount: 0,
  amount_usdt: 0,
  description: '',
  transaction_date: ''
})

// Settlement form
const settlementForm = ref({
  id: null as number | null,
  investor_id: null as number | null,
  wallet_id: null as number | null,
  user_id: null as number | null,
  amount: 0,
  amount_usdt: 0,
  rate: 0,
  direction: 'to_wallet' as 'to_wallet' | 'to_investor',
  transaction_date: '',
  description: ''
})

const settlementWalletSearch = ref('')
const settlementDirectionOptions = [
  { label: 'To Wallet', value: 'to_wallet' },
  { label: 'To Investor', value: 'to_investor' }
]

// Transaction form computed properties
const filteredClients = computed(() => {
  if (!clientSearch.value) return clients.value
  const search = clientSearch.value.toLowerCase()
  return clients.value.filter(client => 
    client.phone.toLowerCase().includes(search)
  )
})

const filteredFromWallets = computed(() => {
  if (!transactionWallets.value || transactionWallets.value.length === 0) return []
  if (!fromWalletSearch.value) return transactionWallets.value
  const search = fromWalletSearch.value.toLowerCase()
  return transactionWallets.value.filter(wallet => {
    const displayName = wallet.display_name || (wallet.exchange?.name ? `${wallet.exchange.name} - ${wallet.coin?.symbol}` : wallet.coin?.symbol) || ''
    return displayName.toLowerCase().includes(search) ||
      wallet.exchange?.name?.toLowerCase().includes(search) ||
      wallet.coin?.symbol?.toLowerCase().includes(search) ||
      wallet.coin?.name?.toLowerCase().includes(search)
  })
})

const filteredToWallets = computed(() => {
  if (!transactionWallets.value || transactionWallets.value.length === 0) return []
  if (!toWalletSearch.value) return transactionWallets.value
  const search = toWalletSearch.value.toLowerCase()
  return transactionWallets.value.filter(wallet => {
    const displayName = wallet.display_name || (wallet.exchange?.name ? `${wallet.exchange.name} - ${wallet.coin?.symbol}` : wallet.coin?.symbol) || ''
    return displayName.toLowerCase().includes(search) ||
      wallet.exchange?.name?.toLowerCase().includes(search) ||
      wallet.coin?.symbol?.toLowerCase().includes(search) ||
      wallet.coin?.name?.toLowerCase().includes(search)
  })
})

const fromWalletBalance = computed(() => {
  if (!transactionForm.value.from_wallet_id || !transactionWallets.value || transactionWallets.value.length === 0) {
    return ''
  }
  const wallet = transactionWallets.value.find(w => w.id === transactionForm.value.from_wallet_id)
  if (!wallet) return ''
  const balance = wallet.calculated_balance ?? 0
  return balance.toString()
})

const soldAmountUsdt = computed(() => {
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  const sendFee = parseFloat(transactionForm.value.send_fee?.toString() || '0')
  if (soldAmount > 0 && soldRate > 0) {
    const usdtValue = (soldAmount - sendFee) * soldRate
    return Math.round(usdtValue * 100000000) / 100000000
  }
  return 0
})

const receivedAmountUsdt = computed(() => {
  const receivedAmount = parseFloat(transactionForm.value.received_amount?.toString() || '0')
  const receivedRate = parseFloat(transactionForm.value.receive_our_rate?.toString() || '0')
  if (receivedAmount > 0 && receivedRate > 0) {
    const usdtValue = receivedAmount * receivedRate
    return Math.round(usdtValue * 100000000) / 100000000
  }
  return 0
})

const shouldShowSaveClientButton = computed(() => {
  return clientSearch.value && clientSearch.value.length >= 10 && !transactionForm.value.client_id
})

// Date filters
const filterDateFrom = ref<string | null>(null)
const filterDateTo = ref<string | null>(null)
const datePeriod = ref<string | null>(null)

// Set initial date period
const setDatePeriod = (period: string) => {
  if (!isInitializingFromURL) {
    if (period === 'custom') {
      // Custom period - just update URL, don't change dates
      updateURL()
    } else {
      const dateRange = getDateRangeForPeriod(period)
      filterDateFrom.value = dateRange.from
      filterDateTo.value = dateRange.to
      updateURL()
    }
  }
}

// Update URL with query params
const updateURL = () => {
  const query: any = {}
  if (filterDateFrom.value) {
    query.date_from = filterDateFrom.value
  }
  if (filterDateTo.value) {
    query.date_to = filterDateTo.value
  }
  if (datePeriod.value) {
    query.period = datePeriod.value
  }
  // Store the query string before updating to detect our own updates
  lastURLUpdate = JSON.stringify(query)
  router.replace({ query })
}

// Flag to prevent watch from firing during URL initialization
let isInitializingFromURL = false

// Watch date period changes
watch(datePeriod, (newPeriod, oldPeriod) => {
  // Only act on period changes if not initializing and period actually changed
  if (!isInitializingFromURL && newPeriod && newPeriod !== oldPeriod) {
    setDatePeriod(newPeriod)
    // Only reload data if it's not 'custom' (custom keeps current dates)
    if (newPeriod !== 'custom') {
      loadWalletData()
    }
  }
})

// Handle manual date filter changes
const onDateFilterChange = () => {
  // Temporarily disable period watch and route watch to prevent resetting dates
  isInitializingFromURL = true
  
  // Check if the current dates match any predefined period
  // Only check if both dates are set
  if (filterDateFrom.value && filterDateTo.value) {
    const dateRangeDay = getDateRangeForPeriod('day')
    const dateRangeWeek = getDateRangeForPeriod('week')
    const dateRangeMonth = getDateRangeForPeriod('month')
    
    if (filterDateFrom.value === dateRangeDay.from && filterDateTo.value === dateRangeDay.to) {
      datePeriod.value = 'day'
    } else if (filterDateFrom.value === dateRangeWeek.from && filterDateTo.value === dateRangeWeek.to) {
      datePeriod.value = 'week'
    } else if (filterDateFrom.value === dateRangeMonth.from && filterDateTo.value === dateRangeMonth.to) {
      datePeriod.value = 'month'
    } else {
      // Custom date range - set period to 'custom'
      datePeriod.value = 'custom'
    }
  } else if (filterDateFrom.value || filterDateTo.value) {
    // If only one date is set, mark as custom
    datePeriod.value = 'custom'
  } else {
    // If dates are cleared, set period to null
    datePeriod.value = null
  }
  
  // Update URL immediately with new date parameters
  updateURL()
  
  // Reset flag after a short delay to allow URL update to complete
  setTimeout(() => {
    isInitializingFromURL = false
  }, 0)
  
  // Load wallet data with new dates
  loadWalletData()
}

const loadWalletData = async () => {
  try {
    loading.value = true
    const walletId = route.params.id
    
    // Validate wallet ID
    if (!walletId || walletId === 'undefined' || walletId === 'null') {
      console.error('Invalid wallet ID:', walletId)
      alert(t('walletDetail.invalidWalletId'))
      router.push('/wallets')
      return
    }
    
    // Ensure walletId is a string/number
    const walletIdStr = String(walletId)
    let url = `/api/wallets/${walletIdStr}`
    
    const params = new URLSearchParams()
    if (filterDateFrom.value) {
      params.append('date_from', filterDateFrom.value)
    }
    if (filterDateTo.value) {
      params.append('date_to', filterDateTo.value)
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`
    }
    
    console.log('Loading wallet data from:', url) // Debug log
    
    const response = await apiGet(url)
    
    if (response.ok) {
      const data = await response.json()
      
      // Validate that we got data for the correct wallet
      if (data.wallet && String(data.wallet.id) !== String(walletIdStr)) {
        console.error('Wallet ID mismatch! Requested:', walletIdStr, 'Got:', data.wallet.id)
        alert('Wallet ID mismatch - received data for different wallet')
        return
      }
      
      wallet.value = data.wallet
      balanceHistory.value = data.balance_history || []
      balanceBreakdown.value = data.balance_breakdown || null
      
      console.log('Wallet data loaded:', {
        walletId: data.wallet?.id,
        balanceHistoryCount: balanceHistory.value.length,
        balanceBreakdown: balanceBreakdown.value
      }) // Debug log
    } else {
      const errorText = await response.text()
      console.error('Failed to load wallet data:', response.status, errorText)
      alert(t('walletDetail.failedToLoad', { status: response.status }))
    }
  } catch (error) {
    console.error('Load wallet error:', error)
    alert(t('walletDetail.errorLoading'))
  } finally {
    loading.value = false
  }
}

// Format amount to 2 decimal places
const formatAmount = (amount: number | null | undefined): string => {
  const value = typeof amount === 'number' ? amount : parseFloat(String(amount || 0))
  if (isNaN(value)) return '0.00'
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })
}

// Format date and time for display
const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Get type color for chip
const getTypeColor = (type: string): string => {
  switch (type) {
    case 'transaction':
      return 'primary'
    case 'expense':
      return 'error'
    case 'settlement':
      return 'info'
    default:
      return 'grey'
  }
}

// Get type label for chip
const getTypeLabel = (type: string): string => {
  switch (type) {
    case 'transaction':
      return t('walletDetail.transaction')
    case 'expense':
      return t('walletDetail.expense')
    case 'settlement':
      return t('walletDetail.settlement')
    default:
      return type
  }
}

// Chart data
const chartData = computed(() => {
  if (!balanceBreakdown.value || !balanceBreakdown.value.daily_balances || balanceBreakdown.value.daily_balances.length === 0) {
    return null
  }
  
  const dailyBalances = balanceBreakdown.value.daily_balances
  
  return {
    labels: dailyBalances.map(d => {
      const date = new Date(d.date)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }),
    datasets: [{
      label: t('walletDetail.balance'),
      data: dailyBalances.map(d => d.balance),
      borderColor: '#3f51b5',
      backgroundColor: 'rgba(63, 81, 181, 0.1)',
      tension: 0.4,
      fill: true
    }]
  }
})

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          return `Balance: ${formatAmount(context.parsed.y)}`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: false,
      ticks: {
        callback: (value: any) => formatAmount(value)
      }
    }
  }
}

// Edit item - load data and open edit dialog
const editItem = async (item: BalanceHistoryItem) => {
  if (item.type === 'transaction') {
    await loadTransaction(item.record_id)
  } else if (item.type === 'expense') {
    await loadExpense(item.record_id)
  } else if (item.type === 'settlement') {
    await loadSettlement(item.record_id)
  }
}

// Helper functions for transaction dialog
const loadClients = async () => {
  try {
    const response = await apiGet('/api/clients')
    if (response.ok) {
      clients.value = await response.json()
    }
  } catch (error) {
    console.error('Load clients error:', error)
  }
}

const ensureWalletsLoaded = async () => {
  if (!transactionWallets.value || transactionWallets.value.length === 0) {
    await loadTransactionWallets()
  }
}

const loadTransactionWallets = async () => {
  try {
    const response = await apiGet('/api/wallets')
    if (response.ok) {
      const walletData = await response.json()
      transactionWallets.value = walletData.map((w: any) => {
        const exchangeName = w.exchange?.name || ''
        const coinSymbol = w.coin?.symbol || ''
        const displayName = exchangeName ? `${exchangeName} - ${coinSymbol}` : coinSymbol
        return {
          ...w,
          display_name: displayName
        }
      })
    }
  } catch (error) {
    console.error('Load transaction wallets error:', error)
  }
}

const loadCurrentUser = async () => {
  try {
    const response = await apiGet('/api/users/current')
    if (response.ok) {
      const userData = await response.json()
      return userData.id
    }
  } catch (error) {
    console.error('Load current user error:', error)
  }
  return null
}

const fetchRateFromDB = async (coinId: number | null, created_at?: string | null) => {
  if (!coinId) return null
  
  try {
    let url = `/api/coin_rates/latest?coin_id=${coinId}`
    if (created_at) {
      url += `&date_before=${created_at}`
    }
    const response = await apiGet(url)
    if (response.ok) {
      const rateData = await response.json()
      return rateData.rate
    }
  } catch (error) {
    console.error('Fetch rate from DB error:', error)
  }
  return null
}

const saveClientFromSearch = async () => {
  if (!clientSearch.value || clientSearch.value.length < 10) {
    alert(t('walletDetail.enterAtLeast10Digits'))
    return
  }
  
  try {
    const response = await apiPost('/api/clients', { client: { phone: clientSearch.value } })
    if (response.ok) {
      const newClient = await response.json()
      transactionForm.value.client_id = newClient.id
      await loadClients()
      // Update search to show the new client
      const client = clients.value.find(c => c.id === newClient.id)
      if (client) {
        clientSearch.value = client.phone
      }
    } else {
      const errorData = await response.json()
      alert(t('walletDetail.failedToCreateClient', { errors: errorData.errors?.join(', ') || t('errors.unknownError') }))
    }
  } catch (error) {
    console.error('Save client from search error:', error)
    alert(t('walletDetail.errorCreatingClient'))
  }
}

const calculateReceivedAmount = () => {
  if (isCalculatingReceivedAmount.value) return
  
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  const receivedRate = parseFloat(transactionForm.value.received_rate?.toString() || '0')
  
  if (soldAmount > 0 && soldRate > 0 && receivedRate > 0) {
    isCalculatingReceivedAmount.value = true
    const calculatedAmount = (soldAmount * soldRate) / receivedRate
    transactionForm.value.received_amount = Math.round(calculatedAmount * 100000000) / 100000000
    setTimeout(() => {
      isCalculatingReceivedAmount.value = false
    }, 0)
  } else if (soldAmount === 0 || soldRate === 0 || receivedRate === 0) {
    transactionForm.value.received_amount = 0
  }
}

const calculateEstimatedProfit = () => {
  if (isCalculatingProfit.value) return
  
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  const receivedAmount = parseFloat(transactionForm.value.received_amount?.toString() || '0')
  const receivedRate = parseFloat(transactionForm.value.receive_our_rate?.toString() || transactionForm.value.received_rate?.toString() || '0')
  const sendFee = parseFloat(transactionForm.value.send_fee?.toString() || '0')
  
  if (soldAmount > 0 && soldRate > 0 && receivedAmount > 0 && receivedRate > 0) {
    isCalculatingProfit.value = true
    const soldValue = soldAmount * soldRate
    const receivedValue = receivedAmount * receivedRate
    const profit = receivedValue - soldValue - sendFee * soldRate
    transactionForm.value.estimated_profit = Math.round(profit * 100000000) / 100000000
    setTimeout(() => {
      isCalculatingProfit.value = false
    }, 0)
  } else {
    transactionForm.value.estimated_profit = 0
  }
}

const calculateReceiveOurRate = () => {
  if (isCalculatingReceiveOurRate.value) return
  
  const soldAmount = parseFloat(transactionForm.value.sold_amount?.toString() || '0')
  const soldRate = parseFloat(transactionForm.value.sold_rate?.toString() || '0')
  const receivedAmount = parseFloat(transactionForm.value.received_amount?.toString() || '0')
  
  if (soldAmount > 0 && soldRate > 0 && receivedAmount > 0) {
    isCalculatingReceiveOurRate.value = true
    const calculatedRate = (soldAmount * soldRate) / receivedAmount
    transactionForm.value.receive_our_rate = Math.round(calculatedRate * 100000000) / 100000000
    setTimeout(() => {
      isCalculatingReceiveOurRate.value = false
    }, 0)
  } else if (soldAmount === 0 || soldRate === 0 || receivedAmount === 0) {
    transactionForm.value.receive_our_rate = 0
  }
}

// Load transaction for editing
const loadTransaction = async (id: number) => {
  try {
    await ensureWalletsLoaded()
    await loadClients()
    
    const response = await apiGet(`/api/transactions/${id}`)
    if (response.ok) {
      const transaction = await response.json()
      editingTransaction.value = transaction
      showEditTransactionDialog.value = true
    } else {
      alert('Failed to load transaction')
    }
  } catch (error) {
    console.error('Load transaction error:', error)
    alert('Error loading transaction')
  }
}

// Load expense for editing
const loadExpense = async (id: number) => {
  try {
    const response = await apiGet(`/api/expenses/${id}`)
    if (response.ok) {
      const expense = await response.json()
      editingExpense.value = expense
      showEditExpenseDialog.value = true
    } else {
      alert('Failed to load expense')
    }
  } catch (error) {
    console.error('Load expense error:', error)
    alert('Error loading expense')
  }
}

// Load settlement for editing
const loadSettlement = async (id: number) => {
  try {
    const response = await apiGet(`/api/settlements/${id}`)
    if (response.ok) {
      const settlement = await response.json()
      editingSettlement.value = settlement
      showEditSettlementDialog.value = true
    } else {
      alert('Failed to load settlement')
    }
  } catch (error) {
    console.error('Load settlement error:', error)
    alert('Error loading settlement')
  }
}

// Delete item
const deleteItem = async (item: BalanceHistoryItem) => {
  const itemType = item.type === 'transaction' ? 'transaction' : item.type === 'expense' ? 'expense' : 'settlement'
  const typeLabel = item.type === 'transaction' ? t('walletDetail.transaction') : item.type === 'expense' ? t('walletDetail.expense') : t('walletDetail.settlement')
  
  if (!confirm(t('walletDetail.areYouSureDelete', { type: typeLabel }))) {
    return
  }
  
  try {
    const response = await apiDelete(`/api/${itemType}s/${item.record_id}`)
    if (response.ok) {
      // Reload wallet data to refresh the balance history
      await loadWalletData()
    } else {
      alert(t('walletDetail.failedToDelete', { type: typeLabel }))
    }
  } catch (error) {
    console.error(`Delete ${itemType} error:`, error)
    alert(t('walletDetail.errorDeleting', { type: typeLabel }))
  }
}

// Load coins and exchanges for edit dialog
const loadCoins = async () => {
  try {
    const response = await apiGet('/api/coins')
    if (response.ok) {
      coins.value = await response.json()
    }
  } catch (error) {
    console.error('Load coins error:', error)
  }
}

const loadExchanges = async () => {
  try {
    const response = await apiGet('/api/exchanges')
    if (response.ok) {
      exchanges.value = await response.json()
    }
  } catch (error) {
    console.error('Load exchanges error:', error)
  }
}


// Edit wallet
const editWallet = () => {
  walletForm.value = {
    exchange_id: wallet.value.exchange?.id || null,
    office: wallet.value.office || false,
    coin_id: wallet.value.coin?.id || null,
    name: wallet.value.name || null,
    address: wallet.value.address || null
  }
  showEditDialog.value = true
}

// Save wallet
const saveWallet = async () => {
  try {
    const walletId = route.params.id
    const body = {
      wallet: {
        exchange_id: walletForm.value.exchange_id,
        office: walletForm.value.office,
        coin_id: walletForm.value.coin_id,
        name: walletForm.value.name || null,
        address: walletForm.value.address || null
      }
    }
    
    const response = await apiPut(`/api/wallets/${walletId}`, body)
    
    if (response.ok) {
      showEditDialog.value = false
      await loadWalletData()
    } else {
      const errorData = await response.json()
      alert(t('wallets.failedToSave', { errors: errorData.errors?.join(', ') || t('errors.unknownError') }))
    }
  } catch (error) {
    console.error('Save wallet error:', error)
    alert(t('wallets.errorSaving'))
  }
}

// Reset wallet form
const resetWalletForm = () => {
  walletForm.value = {
    exchange_id: null,
    office: false,
    coin_id: null,
    name: null,
    address: null
  }
}

// Initialize from URL params
const initializeFromURL = () => {
  isInitializingFromURL = true
  const query = route.query
  
  // If both date_from and date_to are in URL, prioritize them over period
  if (query.date_from && query.date_to) {
    // Set dates from URL
    filterDateFrom.value = (query.date_from as string) || null
    filterDateTo.value = (query.date_to as string) || null
    
    // If period is also in URL and it's not 'custom', check if dates match
    if (query.period && query.period !== 'custom') {
      const period = query.period as string
      const dateRange = getDateRangeForPeriod(period)
      // Only use period if dates match
      if (filterDateFrom.value === dateRange.from && filterDateTo.value === dateRange.to) {
        datePeriod.value = period
      } else {
        // Dates don't match period - use custom
        datePeriod.value = 'custom'
      }
    } else {
      // No period or custom period - detect period from dates
      const dateRangeDay = getDateRangeForPeriod('day')
      const dateRangeWeek = getDateRangeForPeriod('week')
      const dateRangeMonth = getDateRangeForPeriod('month')
      
      if (filterDateFrom.value === dateRangeDay.from && filterDateTo.value === dateRangeDay.to) {
        datePeriod.value = 'day'
      } else if (filterDateFrom.value === dateRangeWeek.from && filterDateTo.value === dateRangeWeek.to) {
        datePeriod.value = 'week'
      } else if (filterDateFrom.value === dateRangeMonth.from && filterDateTo.value === dateRangeMonth.to) {
        datePeriod.value = 'month'
      } else {
        // Custom date range
        datePeriod.value = 'custom'
      }
    }
  } else if (query.period && query.period !== 'custom') {
    // Only use period if dates are not explicitly set
    const period = query.period as string
    datePeriod.value = period
    const dateRange = getDateRangeForPeriod(period)
    filterDateFrom.value = dateRange.from
    filterDateTo.value = dateRange.to
  } else {
    // No URL params - initialize with day period and update URL
    datePeriod.value = 'day'
    const dateRange = getDateRangeForPeriod('day')
    filterDateFrom.value = dateRange.from
    filterDateTo.value = dateRange.to
    // Update URL after setting defaults
    setTimeout(() => updateURL(), 0)
  }
  
  isInitializingFromURL = false
}

// Watch route changes to update when URL changes (e.g., browser back/forward)
// Only react to external URL changes, not our own updates
let lastURLUpdate = ''
watch(() => route.query, (newQuery) => {
  if (!isInitializingFromURL) {
    // Create a string representation of the query to detect if it's our own update
    const queryString = JSON.stringify(newQuery)
    if (queryString !== lastURLUpdate) {
      lastURLUpdate = queryString
      initializeFromURL()
      loadWalletData()
    }
  }
}, { deep: true })

// Load additional data for edit forms
const loadWallets = async () => {
  try {
    const response = await apiGet('/api/wallets')
    if (response.ok) {
      const walletData = await response.json()
      allWallets.value = walletData.map((w: any) => {
        // Use display_name from API if available, otherwise construct it
        const displayName = w.display_name || (w.office ? `Office - ${w.coin?.symbol}` : (w.exchange ? `${w.exchange.name} - ${w.coin?.symbol}` : w.coin?.symbol))
        return {
          ...w,
          display_name: displayName
        }
      })
    }
  } catch (error) {
    console.error('Load wallets error:', error)
  }
}

const loadUsers = async () => {
  try {
    const response = await apiGet('/api/users')
    if (response.ok) {
      users.value = await response.json()
    }
  } catch (error) {
    console.error('Load users error:', error)
  }
}

const loadExpensesTypes = async () => {
  try {
    const response = await apiGet('/api/expenses-types')
    if (response.ok) {
      expensesTypes.value = await response.json()
    }
  } catch (error) {
    console.error('Load expenses types error:', error)
  }
}

const loadInvestors = async () => {
  try {
    const response = await apiGet('/api/investors')
    if (response.ok) {
      investors.value = await response.json()
    }
  } catch (error) {
    console.error('Load investors error:', error)
  }
}

// Filtered wallets for settlement autocomplete
const filteredSettlementWallets = computed(() => {
  if (!settlementWalletSearch.value) {
    return allWallets.value
  }
  const search = settlementWalletSearch.value.toLowerCase()
  return allWallets.value.filter(w => 
    w.display_name?.toLowerCase().includes(search) ||
    w.coin?.symbol?.toLowerCase().includes(search) ||
    w.exchange?.name?.toLowerCase().includes(search)
  )
})

// Calculate expense amount USDT
// Note: usdt_rate removed from wallets, so this calculation would need coin rates instead
// For now, amount_usdt should be entered manually or calculated from coin rates
const calculateExpenseAmountUsdt = () => {
  // This function can be updated to use coin rates if needed
  // For now, amount_usdt should be entered manually
}

// Calculate settlement amount USDT
const calculateSettlementAmountUsdt = () => {
  if (settlementForm.value.amount && settlementForm.value.rate) {
    const amount = parseFloat(settlementForm.value.amount?.toString() || '0')
    const rate = parseFloat(settlementForm.value.rate?.toString() || '0')
    if (!isNaN(amount) && !isNaN(rate) && amount > 0 && rate > 0) {
      settlementForm.value.amount_usdt = Math.round(amount * rate * 100000000) / 100000000
    } else {
      settlementForm.value.amount_usdt = 0
    }
  } else {
    settlementForm.value.amount_usdt = 0
  }
}

// Save transaction handler
const handleSaveTransaction = async (formData: any) => {
  try {
    if (!formData.client_id) {
      alert(t('walletDetail.selectClientOrPhone'))
      return
    }
    
    const url = formData.id ? `/api/transactions/${formData.id}` : '/api/transactions'
    // Prepare transaction data, converting transaction_date to created_at if provided
    const transactionData: any = { 
      ...formData, 
      client_id: formData.client_id,
      sold_coin_id: formData.sold_coin_id,
      received_coin_id: formData.received_coin_id,
      from_wallet_id: formData.from_wallet_id,
      to_wallet_id: formData.to_wallet_id
    }
    // If transaction_date is provided, convert it to created_at format
    if (transactionData.transaction_date) {
      transactionData.created_at = dateStringToISO(transactionData.transaction_date) || new Date().toISOString()
      delete transactionData.transaction_date
    }
    const body = { transaction: transactionData }
    
    const response = formData.id 
      ? await apiPut(url, body)
      : await apiPost(url, body)
    
    if (response.ok) {
      showEditTransactionDialog.value = false
      editingTransaction.value = null
      await loadWalletData()
      await loadClients() // Reload clients in case a new one was created
    } else {
      const errors = await getErrorMessage(response, t('errors.unknownError'))
      alert(t('transactions.errorMessage', { errors }))
    }
  } catch (error) {
    console.error('Save transaction error:', error)
    alert(t('walletDetail.errorSavingTransaction'))
  }
}

const handleCancelTransaction = () => {
  editingTransaction.value = null
}

// Save expense handler
const handleSaveExpense = async (formData: any) => {
  try {
    if (!formData.wallet_id || !formData.expenses_type_id || !formData.user_id || !formData.amount || !formData.amount_usdt || !formData.transaction_date) {
      alert(t('walletDetail.fillAllFields'))
      return
    }

    const expenseData = {
      wallet_id: formData.wallet_id,
      expenses_type_id: formData.expenses_type_id,
      user_id: formData.user_id,
      amount: formData.amount,
      amount_usdt: formData.amount_usdt,
      description: formData.description,
      transaction_date: formData.transaction_date
    }

    const url = formData.id ? `/api/expenses/${formData.id}` : '/api/expenses'
    const response = formData.id
      ? await apiPut(url, { expense: expenseData })
      : await apiPost(url, { expense: expenseData })

    if (response.ok) {
      showEditExpenseDialog.value = false
      editingExpense.value = null
      await loadWalletData()
    } else {
      const errorText = await getErrorMessage(response, t('errors.unknownError'))
      alert(t('walletDetail.failedToSaveExpense', { errors: errorText }))
    }
  } catch (error) {
    console.error('Save expense error:', error)
    alert(t('walletDetail.errorSavingExpense'))
  }
}

const handleCancelExpense = () => {
  editingExpense.value = null
}

// Save settlement handler
const handleSaveSettlement = async (formData: any) => {
  try {
    if (!formData.investor_id || !formData.wallet_id || !formData.user_id || !formData.amount || !formData.amount_usdt || !formData.rate || !formData.direction || !formData.transaction_date) {
      alert(t('walletDetail.fillAllFields'))
      return
    }

    const settlementData = {
      investor_id: formData.investor_id,
      wallet_id: formData.wallet_id,
      user_id: formData.user_id,
      amount: parseFloat(formData.amount?.toString() || '0'),
      amount_usdt: parseFloat(formData.amount_usdt?.toString() || '0'),
      rate: parseFloat(formData.rate?.toString() || '0'),
      direction: formData.direction,
      transaction_date: formData.transaction_date,
      description: formData.description || null
    }

    const url = formData.id ? `/api/settlements/${formData.id}` : '/api/settlements'
    const response = formData.id
      ? await apiPut(url, { settlement: settlementData })
      : await apiPost(url, { settlement: settlementData })

    if (response.ok) {
      showEditSettlementDialog.value = false
      editingSettlement.value = null
      await loadWalletData()
    } else {
      const errorText = await getErrorMessage(response, t('errors.unknownError'))
      alert(t('walletDetail.failedToSaveSettlement', { errors: errorText }))
    }
  } catch (error) {
    console.error('Save settlement error:', error)
    alert(t('walletDetail.errorSavingSettlement'))
  }
}

const handleCancelSettlement = () => {
  editingSettlement.value = null
}

// Reset forms
const resetTransactionForm = async () => {
  if (!showEditTransactionDialog.value) {
    const userId = await loadCurrentUser()
    transactionForm.value = {
      id: null,
      client_id: null,
      from_wallet_id: null,
      sold_coin_id: null,
      sold_amount: 0,
      sold_rate: 0,
      to_wallet_id: null,
      received_coin_id: null,
      received_amount: 0,
      received_rate: 0,
      user_id: userId,
      send_fee: 0,
      receive_our_rate: 0,
      estimated_profit: 0,
      transaction_date: getTodayString(),
    }
    clientSearch.value = ''
    fromWalletSearch.value = ''
    toWalletSearch.value = ''
  }
}

const resetExpenseForm = () => {
  expenseForm.value = {
    id: null,
    wallet_id: null,
    expenses_type_id: null,
    user_id: null,
    amount: 0,
    amount_usdt: 0,
    description: '',
    transaction_date: ''
  }
}

const resetSettlementForm = () => {
  settlementForm.value = {
    id: null,
    investor_id: null,
    wallet_id: null,
    user_id: null,
    amount: 0,
    amount_usdt: 0,
    rate: 0,
    direction: 'to_wallet',
    transaction_date: '',
    description: ''
  }
  settlementWalletSearch.value = ''
}

// Watchers for transaction form
watch(() => transactionForm.value.from_wallet_id, async (newWalletId) => {
  if (!newWalletId) return
  
  await ensureWalletsLoaded()
  
  if (transactionWallets.value && transactionWallets.value.length > 0) {
    const wallet = transactionWallets.value.find(w => w.id === newWalletId)
    if (wallet && wallet.coin && wallet.coin.id) {
      transactionForm.value.sold_coin_id = wallet.coin.id
      const created_at = transactionForm.value.transaction_date 
        ? dateStringToISO(transactionForm.value.transaction_date)
        : null
      const rate = await fetchRateFromDB(wallet.coin.id, created_at)
      if (rate) {
        transactionForm.value.sold_rate = rate
      }
    }
  }
})

watch(() => transactionForm.value.to_wallet_id, async (newWalletId) => {
  if (!newWalletId) return
  
  await ensureWalletsLoaded()
  
  if (transactionWallets.value && transactionWallets.value.length > 0) {
    const wallet = transactionWallets.value.find(w => w.id === newWalletId)
    if (wallet && wallet.coin && wallet.coin.id) {
      transactionForm.value.received_coin_id = wallet.coin.id
      const created_at = transactionForm.value.transaction_date 
        ? dateStringToISO(transactionForm.value.transaction_date)
        : null
      const rate = await fetchRateFromDB(wallet.coin.id, created_at)
      if (rate) {
        transactionForm.value.received_rate = rate
      }
    }
  }
})

watch(() => transactionForm.value.sold_coin_id, async (newCoinId) => {
  if (newCoinId) {
    const created_at = transactionForm.value.transaction_date 
      ? new Date(transactionForm.value.transaction_date).toISOString()
      : null
    const rate = await fetchRateFromDB(newCoinId, created_at)
    if (rate) {
      transactionForm.value.sold_rate = rate
    }
    setTimeout(() => {
      calculateReceiveOurRate()
    }, 100)
  }
})

watch(() => transactionForm.value.received_coin_id, async (newCoinId) => {
  if (newCoinId) {
    const created_at = transactionForm.value.transaction_date 
      ? new Date(transactionForm.value.transaction_date).toISOString()
      : null
    const rate = await fetchRateFromDB(newCoinId, created_at)
    if (rate) {
      transactionForm.value.received_rate = rate
    }
    setTimeout(() => {
      calculateReceiveOurRate()
    }, 100)
  }
})

watch(() => transactionForm.value.transaction_date, async (newDate) => {
  if (newDate && transactionForm.value.sold_coin_id) {
    const created_at = new Date(newDate).toISOString()
    const rate = await fetchRateFromDB(transactionForm.value.sold_coin_id, created_at)
    if (rate) {
      transactionForm.value.sold_rate = rate
    }
  }
  if (newDate && transactionForm.value.received_coin_id) {
    const created_at = new Date(newDate).toISOString()
    const rate = await fetchRateFromDB(transactionForm.value.received_coin_id, created_at)
    if (rate) {
      transactionForm.value.received_rate = rate
    }
  }
})

watch(() => transactionForm.value.sold_amount, () => {
  calculateReceivedAmount()
  calculateEstimatedProfit()
  setTimeout(() => {
    calculateReceiveOurRate()
  }, 0)
})

watch(() => transactionForm.value.sold_rate, () => {
  calculateReceivedAmount()
  calculateEstimatedProfit()
  setTimeout(() => {
    calculateReceiveOurRate()
  }, 0)
})

watch(() => transactionForm.value.received_amount, () => {
  calculateEstimatedProfit()
  calculateReceiveOurRate()
})

watch(() => transactionForm.value.received_rate, () => {
  calculateReceivedAmount()
  calculateEstimatedProfit()
  setTimeout(() => {
    calculateReceiveOurRate()
  }, 0)
})

watch(() => transactionForm.value.receive_our_rate, () => {
  calculateReceivedAmount()
  calculateEstimatedProfit()
})

watch(() => transactionForm.value.send_fee, () => {
  calculateEstimatedProfit()
})

onMounted(async () => {
  // Load coins, exchanges, and offices for edit dialog
  await loadCoins()
  await loadExchanges()
  // Load data for item edit forms
  await loadWallets()
  await loadUsers()
  await loadExpensesTypes()
  await loadInvestors()
  await loadClients()
  await loadTransactionWallets()
  
  // Initialize from URL params
  initializeFromURL()
  
  // Load wallet data
  await loadWalletData()
})
</script>

<style scoped>
.period-buttons {
  width: 100%;
}

.border-top {
  border-top: 2px solid rgba(0, 0, 0, 0.12);
}

.filters-wrapper {
  position: relative;
  margin-bottom: 36px;
}

.date-from-wrapper {
  position: relative;
  display: inline-block;
}

.period-buttons-container {
  position: absolute;
  top: 100%;
  right: 70px;
  margin-top: 4px;
  margin-bottom: 12px;
}

.period-buttons :deep(.v-btn) {
  margin: 0;
  padding: 4px 12px;
  min-width: auto;
  height: 24px;
  max-height: 24px;
}

.period-buttons :deep(.v-btn-toggle__content) {
  gap: 0;
}
</style>
